import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Bot,
  Check,
  Zap,
  Building2,
  ArrowRight,
  Shield,
  Clock,
  Users,
  Sparkles
} from 'lucide-react';
import { ROUTES } from '@/lib/constants';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/lib/translations';
import { animations } from '@/lib/animations';

const PricingPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');

  const plans = [
    {
      name: 'Starter (POC)',
      description: 'Perfect for validating our solution in your business',
      price: billingPeriod === 'monthly' ? '$999' : '$899',
      period: '/month',
      features: [
        'Implementation in one business area',
        '30-60 day trial period',
        'Dedicated support during POC',
        'Up to 1,000 AI interactions/month',
        'Basic analytics dashboard',
        'ROI evaluation report',
        'Email support'
      ],
      icon: Bot,
      cta: 'Start POC',
      route: ROUTES.START_POC
    },
    {
      name: 'Professional',
      description: 'Ideal for growing businesses ready to scale',
      price: billingPeriod === 'monthly' ? '$2,499' : '$2,249',
      period: '/month',
      features: [
        'Implementation in multiple areas',
        'Unlimited AI interactions',
        'Advanced analytics & reporting',
        'Custom AI model training',
        'API access',
        'Priority support',
        'Dedicated success manager'
      ],
      icon: Zap,
      popular: true,
      cta: 'Talk to Sales',
      route: ROUTES.CONTACT_SALES
    },
    {
      name: 'Enterprise',
      description: 'Custom solutions for large organizations',
      price: 'Custom',
      features: [
        'Full enterprise implementation',
        'Custom AI model development',
        'Dedicated infrastructure',
        'Advanced security features',
        'SLA guarantees',
        '24/7 premium support',
        'Custom integrations'
      ],
      icon: Building2,
      cta: 'Contact Us',
      route: ROUTES.CONTACT_SALES
    }
  ];

  const features = [
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Bank-grade encryption and security measures to protect your data'
    },
    {
      icon: Clock,
      title: 'Quick Implementation',
      description: 'Get started in days, not months, with our streamlined process'
    },
    {
      icon: Users,
      title: 'Dedicated Support',
      description: 'Expert guidance and support throughout your journey'
    },
    {
      icon: Sparkles,
      title: 'Custom AI Models',
      description: 'AI solutions tailored to your specific business needs'
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <section className="flex flex-col items-center justify-center py-16 md:py-24 relative overflow-hidden w-full bg-gradient-to-b from-primary/5 to-transparent">
        <div className="relative mx-auto flex max-w-[1200px] flex-col items-center gap-8 text-center px-4">
          <h1 className={`text-4xl font-bold leading-tight tracking-tighter md:text-6xl lg:leading-[1.1] ${animations.fadeInSlideUp}`}>
            Simple, Transparent Pricing
          </h1>
          <p className={`max-w-[750px] text-lg text-muted-foreground md:text-xl ${animations.fadeInSlideUp} ${animations.delay200}`}>
            Start with a proof of concept and scale as your needs grow
          </p>

          <div className={`flex items-center gap-4 ${animations.fadeInSlideUp} ${animations.delay300}`}>
            <Button
              variant={billingPeriod === 'monthly' ? 'default' : 'outline'}
              onClick={() => setBillingPeriod('monthly')}
              className="w-32"
            >
              Monthly
            </Button>
            <Button
              variant={billingPeriod === 'annual' ? 'default' : 'outline'}
              onClick={() => setBillingPeriod('annual')}
              className="w-32"
            >
              Annual
              <span className="ml-2 text-xs bg-primary/20 px-2 py-0.5 rounded-full">
                Save 10%
              </span>
            </Button>
          </div>
        </div>
      </section>

      <section className="container py-12">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 max-w-[1200px] mx-auto">
          {plans.map((plan, index) => {
            const Icon = plan.icon;
            return (
              <Card
                key={plan.name}
                className={`relative p-8 ${plan.popular ? 'border-primary shadow-lg scale-105' : 'border-border'} ${animations.fadeInSlideUp}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </div>
                )}
                <div className="flex flex-col gap-6">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{plan.name}</h3>
                      <p className="text-sm text-muted-foreground">{plan.description}</p>
                    </div>
                  </div>

                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    {plan.period && (
                      <span className="text-muted-foreground">{plan.period}</span>
                    )}
                  </div>

                  <ul className="space-y-3 flex-grow">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    size="lg"
                    className="w-full group"
                    variant={plan.popular ? 'default' : 'outline'}
                    onClick={() => navigate(plan.route)}
                  >
                    {plan.cta}
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      <section className="container py-24 bg-muted/30">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Enterprise-Grade Features</h2>
            <p className="text-lg text-muted-foreground">
              Every plan includes these powerful features to help your business succeed
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card
                  key={feature.title}
                  className={`p-6 ${animations.fadeInSlideUp}`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex flex-col gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg w-fit">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="container py-24">
        <div className="max-w-[900px] mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">Need a Custom Solution?</h2>
          <p className="text-lg text-muted-foreground mb-8">
            Contact our sales team to discuss your specific requirements and get a tailored solution for your business
          </p>
          <Button
            size="lg"
            className="group"
            onClick={() => navigate(ROUTES.CONTACT_SALES)}
          >
            Talk to Sales
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default PricingPage; 